import React, { useState } from 'react';
import {
  Users, Calendar, Clock, MapPin, Star, User, Plus,
  Search, Filter, Video, MessageCircle, BookOpen,
  Target, TrendingUp, Award, Shield, Globe, Lock,
  UserPlus, Settings, Share2, Flag, Eye, EyeOff,
  ChevronRight, Zap, Brain, Coffee, Lightbulb,
  Code, Database, Palette, BarChart3, Heart,
  CheckCircle, AlertCircle, Timer, Bookmark
} from 'lucide-react';

interface StudyGroup {
  id: string;
  name: string;
  description: string;
  topic: string;
  category: string;
  level: 'beginner' | 'intermediate' | 'advanced' | 'mixed';
  members: number;
  maxMembers: number;
  isPublic: boolean;
  tags: string[];
  meetingType: 'online' | 'hybrid' | 'in-person';
  schedule: {
    frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'flexible';
    day?: string;
    time?: string;
    timezone?: string;
  };
  nextMeeting?: {
    date: string;
    time: string;
    topic: string;
  };
  organizer: {
    name: string;
    avatar: string;
    role: 'student' | 'instructor' | 'mentor';
    reputation: number;
  };
  createdAt: string;
  isJoined: boolean;
  rating: number;
  reviews: number;
  languages: string[];
  status: 'active' | 'full' | 'archived';
}

export default function StudyGroups() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [showOnlyJoined, setShowOnlyJoined] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const studyGroups: StudyGroup[] = [
    {
      id: '1',
      name: 'React Mastery Circle',
      description: 'Advanced React concepts, patterns, and best practices. We meet weekly to discuss complex topics, share projects, and help each other grow.',
      topic: 'Advanced React Development',
      category: 'Development',
      level: 'advanced',
      members: 12,
      maxMembers: 15,
      isPublic: true,
      tags: ['react', 'javascript', 'frontend', 'hooks', 'performance'],
      meetingType: 'online',
      schedule: {
        frequency: 'weekly',
        day: 'Wednesday',
        time: '7:00 PM',
        timezone: 'EST'
      },
      nextMeeting: {
        date: '2024-02-07',
        time: '7:00 PM EST',
        topic: 'React Server Components Deep Dive'
      },
      organizer: {
        name: 'Sarah Chen',
        avatar: '👩‍💻',
        role: 'instructor',
        reputation: 1250
      },
      createdAt: '2024-01-15',
      isJoined: true,
      rating: 4.8,
      reviews: 24,
      languages: ['English'],
      status: 'active'
    },
    {
      id: '2',
      name: 'Data Science Beginners',
      description: 'Perfect for those starting their data science journey. We cover Python basics, statistics, and intro to machine learning.',
      topic: 'Data Science Fundamentals',
      category: 'Data Science',
      level: 'beginner',
      members: 8,
      maxMembers: 20,
      isPublic: true,
      tags: ['python', 'statistics', 'pandas', 'machine-learning', 'beginner'],
      meetingType: 'online',
      schedule: {
        frequency: 'bi-weekly',
        day: 'Saturday',
        time: '10:00 AM',
        timezone: 'PST'
      },
      nextMeeting: {
        date: '2024-02-10',
        time: '10:00 AM PST',
        topic: 'Introduction to NumPy and Pandas'
      },
      organizer: {
        name: 'Dr. Michael Roberts',
        avatar: '👨‍🔬',
        role: 'instructor',
        reputation: 2100
      },
      createdAt: '2024-01-10',
      isJoined: false,
      rating: 4.9,
      reviews: 18,
      languages: ['English', 'Spanish'],
      status: 'active'
    },
    {
      id: '3',
      name: 'UI/UX Design Collective',
      description: 'A creative space for designers to share work, get feedback, and discuss the latest design trends and tools.',
      topic: 'User Interface & Experience Design',
      category: 'Design',
      level: 'intermediate',
      members: 15,
      maxMembers: 15,
      isPublic: true,
      tags: ['design', 'ui', 'ux', 'figma', 'prototyping'],
      meetingType: 'online',
      schedule: {
        frequency: 'weekly',
        day: 'Friday',
        time: '6:00 PM',
        timezone: 'EST'
      },
      nextMeeting: {
        date: '2024-02-09',
        time: '6:00 PM EST',
        topic: 'Design Systems and Component Libraries'
      },
      organizer: {
        name: 'Emma Wilson',
        avatar: '🎨',
        role: 'mentor',
        reputation: 890
      },
      createdAt: '2024-01-08',
      isJoined: false,
      rating: 4.6,
      reviews: 31,
      languages: ['English'],
      status: 'full'
    },
    {
      id: '4',
      name: 'Career Transition Support',
      description: 'Support group for professionals transitioning into tech. Share experiences, network, and get advice on career changes.',
      topic: 'Career Development',
      category: 'Career',
      level: 'mixed',
      members: 23,
      maxMembers: 30,
      isPublic: true,
      tags: ['career-change', 'networking', 'job-search', 'interviews', 'resume'],
      meetingType: 'hybrid',
      schedule: {
        frequency: 'monthly',
        day: 'First Sunday',
        time: '3:00 PM',
        timezone: 'EST'
      },
      nextMeeting: {
        date: '2024-02-04',
        time: '3:00 PM EST',
        topic: 'Networking Strategies for Tech Careers'
      },
      organizer: {
        name: 'Alex Kumar',
        avatar: '💼',
        role: 'mentor',
        reputation: 650
      },
      createdAt: '2023-12-15',
      isJoined: true,
      rating: 4.7,
      reviews: 45,
      languages: ['English'],
      status: 'active'
    },
    {
      id: '5',
      name: 'Machine Learning Study Buddies',
      description: 'Intermediate ML practitioners working through complex algorithms, research papers, and practical implementations.',
      topic: 'Machine Learning & AI',
      category: 'Data Science',
      level: 'intermediate',
      members: 9,
      maxMembers: 12,
      isPublic: false,
      tags: ['machine-learning', 'algorithms', 'tensorflow', 'pytorch', 'research'],
      meetingType: 'online',
      schedule: {
        frequency: 'weekly',
        day: 'Tuesday',
        time: '8:00 PM',
        timezone: 'EST'
      },
      nextMeeting: {
        date: '2024-02-06',
        time: '8:00 PM EST',
        topic: 'Transformer Architecture Analysis'
      },
      organizer: {
        name: 'Dr. Lisa Park',
        avatar: '🧠',
        role: 'instructor',
        reputation: 1800
      },
      createdAt: '2024-01-20',
      isJoined: false,
      rating: 4.9,
      reviews: 12,
      languages: ['English', 'Korean'],
      status: 'active'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Categories', icon: <Users className="w-4 h-4" />, count: studyGroups.length },
    { id: 'Development', name: 'Development', icon: <Code className="w-4 h-4" />, count: studyGroups.filter(g => g.category === 'Development').length },
    { id: 'Data Science', name: 'Data Science', icon: <Database className="w-4 h-4" />, count: studyGroups.filter(g => g.category === 'Data Science').length },
    { id: 'Design', name: 'Design', icon: <Palette className="w-4 h-4" />, count: studyGroups.filter(g => g.category === 'Design').length },
    { id: 'Career', name: 'Career', icon: <Target className="w-4 h-4" />, count: studyGroups.filter(g => g.category === 'Career').length },
    { id: 'Business', name: 'Business', icon: <BarChart3 className="w-4 h-4" />, count: 0 }
  ];

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'text-green-600 bg-green-50 border-green-200';
      case 'intermediate': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'advanced': return 'text-red-600 bg-red-50 border-red-200';
      case 'mixed': return 'text-purple-600 bg-purple-50 border-purple-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getMeetingTypeIcon = (type: string) => {
    switch (type) {
      case 'online': return <Video className="w-4 h-4" />;
      case 'hybrid': return <Globe className="w-4 h-4" />;
      case 'in-person': return <MapPin className="w-4 h-4" />;
      default: return <Video className="w-4 h-4" />;
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'instructor': return <Award className="w-4 h-4 text-yellow-500" />;
      case 'mentor': return <Shield className="w-4 h-4 text-blue-500" />;
      default: return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const filteredGroups = studyGroups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          group.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          group.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          group.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || group.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || group.level === selectedLevel;
    const matchesJoined = !showOnlyJoined || group.isJoined;
    return matchesSearch && matchesCategory && matchesLevel && matchesJoined;
  });

  const myGroups = studyGroups.filter(group => group.isJoined);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Users className="w-7 h-7" />
              Study Groups
            </h1>
            <p className="text-blue-100">
              Join collaborative learning groups or create your own study community
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowOnlyJoined(!showOnlyJoined)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showOnlyJoined 
                  ? 'bg-white text-blue-600' 
                  : 'bg-white/20 backdrop-blur-sm hover:bg-white/30'
              }`}
            >
              <Heart className="w-5 h-5" />
              <span className="hidden sm:inline">My Groups</span>
            </button>
            <button
              onClick={() => setShowCreateGroup(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Create Group</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-blue-200" />
            <p className="text-2xl font-bold">{studyGroups.length}</p>
            <p className="text-xs text-blue-100">Active Groups</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <UserPlus className="w-6 h-6 mx-auto mb-2 text-blue-200" />
            <p className="text-2xl font-bold">{studyGroups.reduce((sum, group) => sum + group.members, 0)}</p>
            <p className="text-xs text-blue-100">Total Members</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Heart className="w-6 h-6 mx-auto mb-2 text-blue-200" />
            <p className="text-2xl font-bold">{myGroups.length}</p>
            <p className="text-xs text-blue-100">My Groups</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Calendar className="w-6 h-6 mx-auto mb-2 text-blue-200" />
            <p className="text-2xl font-bold">12</p>
            <p className="text-xs text-blue-100">This Week</p>
          </div>
        </div>
      </div>

      {/* My Study Groups */}
      {myGroups.length > 0 && !showOnlyJoined && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">My Study Groups</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myGroups.slice(0, 2).map((group) => (
                <div key={group.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">{group.name}</h3>
                      <p className="text-sm text-gray-600">{group.members}/{group.maxMembers} members</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-medium">{group.rating}</span>
                    </div>
                  </div>
                  {group.nextMeeting && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Next Meeting</p>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        <span>{group.nextMeeting.date} at {group.nextMeeting.time}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{group.nextMeeting.topic}</p>
                    </div>
                  )}
                  <button className="w-full px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                    View Group
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-3 rounded-lg border text-center transition-all ${
                  selectedCategory === category.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex justify-center mb-2">{category.icon}</div>
                <p className="text-sm font-medium">{category.name}</p>
                <p className="text-xs text-gray-500">{category.count} groups</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search study groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="mixed">Mixed Level</option>
          </select>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <div className="grid grid-cols-2 gap-0.5 w-4 h-4">
                <div className="bg-current rounded-sm"></div>
                <div className="bg-current rounded-sm"></div>
                <div className="bg-current rounded-sm"></div>
                <div className="bg-current rounded-sm"></div>
              </div>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <div className="space-y-1 w-4 h-4">
                <div className="bg-current h-0.5 rounded"></div>
                <div className="bg-current h-0.5 rounded"></div>
                <div className="bg-current h-0.5 rounded"></div>
                <div className="bg-current h-0.5 rounded"></div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Study Groups */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGroups.map((group) => (
                <div key={group.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{group.name}</h3>
                        {!group.isPublic && <Lock className="w-4 h-4 text-gray-500" />}
                        {group.isJoined && <CheckCircle className="w-4 h-4 text-green-600" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{group.description}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="text-2xl">{group.organizer.avatar}</div>
                        <div>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{group.organizer.name}</span>
                            {getRoleIcon(group.organizer.role)}
                          </div>
                          <p className="text-xs text-gray-500">{group.organizer.reputation} rep</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="font-medium">{group.rating}</span>
                        <span className="text-gray-500">({group.reviews})</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span>{group.members}/{group.maxMembers} members</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {getMeetingTypeIcon(group.meetingType)}
                        <span className="capitalize">{group.meetingType}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full border font-medium ${getLevelColor(group.level)}`}>
                        {group.level}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {group.category}
                      </span>
                    </div>

                    {group.nextMeeting && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-600 mb-1">Next Meeting</p>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span>{group.nextMeeting.date} at {group.nextMeeting.time}</span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1 line-clamp-1">{group.nextMeeting.topic}</p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1">
                      {group.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          #{tag}
                        </span>
                      ))}
                      {group.tags.length > 3 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          +{group.tags.length - 3}
                        </span>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2">
                      {group.status === 'full' ? (
                        <button disabled className="flex-1 px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed">
                          Full
                        </button>
                      ) : group.isJoined ? (
                        <button className="flex-1 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
                          View Group
                        </button>
                      ) : (
                        <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                          Join Group
                        </button>
                      )}
                      <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                        <Bookmark className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredGroups.map((group) => (
                <div key={group.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                  <div className="flex items-start gap-6">
                    <div className="text-4xl">{group.organizer.avatar}</div>
                    
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
                            {!group.isPublic && <Lock className="w-4 h-4 text-gray-500" />}
                            {group.isJoined && <CheckCircle className="w-4 h-4 text-green-600" />}
                          </div>
                          <p className="text-gray-600 mb-3">{group.description}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span className="font-medium">{group.rating}</span>
                          <span className="text-gray-500 text-sm">({group.reviews})</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-gray-500" />
                          <span>{group.members}/{group.maxMembers} members</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getMeetingTypeIcon(group.meetingType)}
                          <span className="capitalize">{group.meetingType}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span>{group.schedule.frequency}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getRoleIcon(group.organizer.role)}
                          <span>{group.organizer.name}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 text-xs rounded-full border font-medium ${getLevelColor(group.level)}`}>
                            {group.level}
                          </span>
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {group.category}
                          </span>
                          <div className="flex gap-1">
                            {group.tags.slice(0, 3).map((tag) => (
                              <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {group.status === 'full' ? (
                            <button disabled className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed">
                              Full
                            </button>
                          ) : group.isJoined ? (
                            <button className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
                              View Group
                            </button>
                          ) : (
                            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                              Join Group
                            </button>
                          )}
                          <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                            <Bookmark className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                            <Share2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredGroups.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No study groups found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your search or create a new study group.</p>
              <button
                onClick={() => setShowCreateGroup(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Study Group
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Create Study Group</h3>
                <button
                  onClick={() => setShowCreateGroup(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
                  <input
                    type="text"
                    placeholder="Enter a catchy name for your study group..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    placeholder="Describe what your group will focus on and what members can expect..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Select category</option>
                      <option value="Development">Development</option>
                      <option value="Data Science">Data Science</option>
                      <option value="Design">Design</option>
                      <option value="Career">Career</option>
                      <option value="Business">Business</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                    <select className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="mixed">Mixed Level</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Members</label>
                    <input
                      type="number"
                      min="3"
                      max="50"
                      defaultValue="15"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type</label>
                    <select className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="online">Online</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="in-person">In-Person</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <input
                    type="text"
                    placeholder="Add tags separated by commas (e.g. react, javascript, beginner)"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isPublic"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="isPublic" className="text-sm text-gray-700">
                    Make this group public (anyone can join)
                  </label>
                </div>
                
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateGroup(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Create Group
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}